import Rails from "@rails/ujs"
import AttachedImageController from "./attached_image_controller"
import { showOverlay, hideOverlay, scrollToFirstError, buildValidationFormData, postValidation, failureMessage } from '../services/form_helper'

// 予約フローの問診ステップ（/users/reservations/:id/new_interview）。
//
// YAS-31（.steering/20260814-ios-empty-body-rollout/design.md）:
// 症状写真のアップロードは添付資料（attached-image / YAS-16）と同じ partial・同じ DOM 規約のため、
// attached_image_controller を基底クラスとして継承し、モデル名の差分だけ getter で吸収する。
// これにより以下が同時に入る:
//   - 最小 FormData 化（フォーム全体ではなく当該フィールドだけを送る）
//   - change 直後のメモリ化（iOS WebKit の空body送信対策）
//   - error ハンドラ・空body 1回リトライ・アップロード後の file input クリア
//   - 「＋」の1フィールド追記化と枚数上限のサーバ定数化（YAS-33 を定数・文言だけに縮小するため）
// 問診に固有のロジック（validate / validateAll / 喫煙本数 / チェックボックス排他）はこのクラスに残す。
export default class extends AttachedImageController {
  static targets = ['smokingNumber'];
  static values = {validateurl: String, maxFields: Number};

  get modelName(){ return "diagnostic_images"; }
  get responseIdKey(){ return "di_id"; }
  get uploadFieldId(){ return "diagnostic_image_upload_field"; }

  formTargetConnected() {
    this.toggleSmokingNumberInput();
    const indexField = this.el("image_index");
    if(indexField != null){ this.updateAddButton(Number(indexField.value)); }
  }

  // 基底の addUploadField から採番直後に呼ばれる
  afterAddField(nextIndex){
    this.updateAddButton(nextIndex);
  }

  // 上限はサーバ定数（ReservationImageUpload::MAX_DIAGNOSTIC_IMAGE_FIELDS）を唯一の正とし、
  // ビューが max-fields-value として出力する。旧実装の `index >= 2` のハードコードは撤去した。
  maxFieldCount(){
    return (this.hasMaxFieldsValue && this.maxFieldsValue > 0) ? this.maxFieldsValue : 2;
  }

  updateAddButton(index){
    if(!this.hasAddButtonTarget){ return; }
    if(index >= this.maxFieldCount()){
      this.addButtonTarget.classList.add('is-hidden');
    }else{
      this.addButtonTarget.classList.remove('is-hidden');
    }
  }

  // ---- 検証 ---------------------------------------------------------------

  validate(event){
    const el = event.currentTarget;
    const pa = el.parentElement;
    const params = event.params;
    let errorM = pa.querySelector("."+this.errormClass);
    if(errorM == undefined){
      errorM = pa.parentElement.querySelector("."+this.errormClass);
      if(errorM == undefined){
        errorM = pa.parentElement.parentElement.querySelector("."+this.errormClass);
      }
    }
    if(errorM == undefined){ return; }
    // YAS-31 FR-2: 実ファイルではなく1バイトの代理ファイルを送る（空body送信の回避）
    const formData = buildValidationFormData(this.formTarget);
    formData.append("type",params.column);
    Rails.ajax({
      url: this.validateurlValue,
      type: "POST",
      dataType: 'json',
      data: formData,
      success: (response)=> {
        errorM.innerHTML = response;
      }
    });
  }

  validateAll(){
    const allErrorE = this.el("allError");
    // YAS-31 統合［中1］: 「確定する」は type="button" のため、基底の beginRequest()（submit ボタンの
    // 無効化）ではガードできない。アップロード飛行中に押すと hidden の diagnostic_images[id][N] が
    // 空のまま次ステップへ進み、写真が紐づかない（孤児 DiagnosticImage）。飛行中は進めない。
    if(this.inFlight > 0){
      this.showAllError(allErrorE, this.uploadingMessageValue || "");
      return;
    }
    showOverlay();
    let errorMs = document.querySelectorAll("."+this.errormClass);
    // YAS-31 FR-3: 旧実装は error コールバックが無く、検証AJAX が失敗するとオーバーレイ表示のまま
    // 固着していた（本番実測 月68件）。失敗時は必ず操作可能な状態へ戻す。
    postValidation({
      url: this.validateurlValue,
      buildFormData: () => buildValidationFormData(this.formTarget),
      onSuccess: (response)=> {
        if(Object.keys(response).length > 0){
          let allErrorTxt = "";
          Object.keys(response).forEach(function (key) {
            errorMs.forEach(function (m) {
              if (m.dataset.errormtarget == key){
                m.innerHTML = response[key];
                allErrorTxt += response[key]+"<br />";
              }
            })
          });
          this.showAllError(allErrorE, allErrorTxt);
          hideOverlay();
          scrollToFirstError(this.errormClass);
        }else{
          this.clearAllError(allErrorE);
          this.formTarget.submit();
        }
      },
      onFailure: (response)=> {
        this.showAllError(allErrorE, failureMessage(response, this.networkErrorMessageValue));
        hideOverlay();
      }
    });
  }

  showAllError(allErrorE, html){
    if(allErrorE == null){ return; }
    allErrorE.innerHTML = html;
    allErrorE.classList.remove(this.hideClass);
  }

  clearAllError(allErrorE){
    if(allErrorE == null){ return; }
    allErrorE.innerHTML = '';
    allErrorE.classList.add(this.hideClass);
  }

  // ---- 問診の入力補助 -----------------------------------------------------

  checkboxDisable(event){
    const el = event.currentTarget;
    const selectors = document.getElementsByName(el.name);
    if(el.checked == true){
      selectors.forEach(function(s){
        s.checked = false;
        s.disabled = true;
      });
      el.checked = true;
      el.disabled = false;
    }else{
      selectors.forEach(function(s){
        s.disabled = false;
      });
    }
  }

  toggleSmokingNumberInput(){
    const form = this.formTarget;
    const smoking = form.querySelector("input[name='diagnostic[smoking]']:checked");
    if(smoking != undefined){
      if(smoking.value == "not"){
        this.smokingNumberTarget.classList.add(this.hideClass);
      }else{
        this.smokingNumberTarget.classList.remove(this.hideClass);
      }
    }
  }
}
